import DocumentPreviewSkeleton from "components/Skeleton/DocumentPreviewSkeleton";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMaterialUIController } from "context";
import {
  LuArrowLeft,
  LuDownload,
  LuPrinter,
  LuRefreshCw,
} from "react-icons/lu";
import { getRCTIByID, downloadRCTI } from "utils/invoiceAPI";
import { savePdfResponse } from "utils/documentPdf";
import RCTIDocument from "./RCTIDocument";
import "layouts/Invoices/invoiceFlow.css";
import "./rctiDetail.css";
import "./rctiResponsive.css";

export default function PreviewRCTI() {
  const { id, version } = useParams();
  const navigate = useNavigate(),
    dispatch = useDispatch();
  const [controller] = useMaterialUIController();
  const [values, setValues] = useState(null),
    [error, setError] = useState(""),
    [downloading, setDownloading] = useState(false);
  const load = useCallback(() => {
    setError("");
    setValues(null);
    getRCTIByID(
      { rctiID: id },
      (result) => {
        if (result?.Result_Status && result.Result_Output?.[0]) {
          const current = result.Result_Output[0];
          if (!version || Number(version) === Number(current.version || 1))
            setValues(current);
          else {
            const revision = current.versions?.find(
              (row) => String(row.version) === version
            );
            if (!revision?.document) {
              setError("RCTI version not found.");
              setValues(null);
              return;
            }
            const saved = revision.document;
            setValues({
              ...saved,
              version: revision.version,
              itemsList: revision.items || [],
              documentsList: [],
              businessName: saved.buyerIdentity?.name || current.businessName,
              buyerAbn: saved.buyerIdentity?.abnNumber || current.buyerAbn,
              supplierCompanyName:
                saved.supplierIdentity?.name || current.supplierCompanyName,
              supplierAbn:
                saved.supplierIdentity?.abnNumber || current.supplierAbn,
              lifecycleStatus: "invalidated",
              invalidationReason: revision.adjustmentReason,
            });
          }
        } else
          setError(result?.Result_Message || "The RCTI could not be loaded.");
      },
      dispatch,
      (result) =>
        setError(result?.Result_Message || "The RCTI could not be loaded.")
    );
  }, [id, version, dispatch]);
  useEffect(() => {
    load();
  }, [load]);
  const currency = /^[A-Z]{3}$/.test(values?.currencyValue)
    ? values.currencyValue
    : "AUD";
  const money = (value) =>
    new Intl.NumberFormat("en-AU", { style: "currency", currency }).format(
      Number(value || 0)
    );
  const download = async () => {
    setDownloading(true);
    try {
      await downloadRCTI(
        { _id: id, ...(version ? { version: Number(version) } : {}) },
        (response) =>
          savePdfResponse(
            response,
            `${values.rctiNumber}-v${values.version || 1}.pdf`
          ),
        dispatch
      );
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div
      className={`invoice-flow-shell invoice-detail-shell rcti-detail-shell rcti-preview-shell${
        controller.darkMode ? " dark" : ""
      }`}
    >
      <main className="rcti-preview-page">
        <header className="rcti-preview-toolbar">
          <button type="button" onClick={() => navigate(-1)}>
            <LuArrowLeft />
            Back
          </button>
          <div>
            <h1>{version ? "RCTI version history" : "RCTI preview"}</h1>
            <p>
              {values?.rctiNumber || "Loading document"}
              {values && ` · Version ${values.version || 1}`}
            </p>
          </div>
          <div className="rcti-preview-toolbar__actions">
            <button
              type="button"
              disabled={!values || downloading}
              onClick={() => window.print()}
            >
              <LuPrinter />
              Print
            </button>
            <button
              className="primary"
              type="button"
              disabled={!values || downloading}
              onClick={download}
            >
              <LuDownload />
              {downloading ? "Preparing PDF..." : "Download PDF"}
            </button>
          </div>
        </header>
        {error ? (
          <section className="rcti-preview-error" role="alert">
            <p>{error}</p>
            <button onClick={load}>
              <LuRefreshCw />
              Retry
            </button>
          </section>
        ) : !values ? (
          <DocumentPreviewSkeleton label="Loading RCTI preview" />
        ) : (
          <RCTIDocument
            values={values}
            items={values.itemsList || []}
            documents={values.documentsList || []}
            organisation={values.businessName || "Your business"}
            rctiID={id}
            loadingPage={false}
            currencyCode={currency}
            subtotalExcludingGst={
              Number(values.finalAmount ?? values.totalAmount ?? 0) -
              Number(values.totalGSTAmount || 0)
            }
            gstSummaryLabel={
              values.gstType === "Exclusive" ? "GST" : "GST (included)"
            }
            total={Number(values.finalAmount ?? values.totalAmount ?? 0)}
            money={money}
            dateValue={(value) =>
              value
                ? new Date(value).toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    timeZone: "UTC",
                  })
                : "-"
            }
          />
        )}
      </main>
    </div>
  );
}
